import { useQuery } from '@apollo/client/react'
import {
  AssociationResult,
  LoadingState,
  MatchingResult,
  RichText,
  ReviewQuestion,
  Section,
  useTranslate,
} from '@athena/ui'

import { MyCompletedAttemptDocument } from '../../graphql/generated/operations.js'

export interface CompletionReviewProps {
  attemptId: string
}

/**
 * What happened, question by question, once a practice is finished for good.
 *
 * The server decides whether this may be seen at all — the answers open only
 * when no further attempt is possible — so a refusal here is a plain message
 * rather than a screen that quietly renders less. Nothing is filtered on this
 * side: a question they got right arrives with no answer and no explanation
 * attached, because the server did not send them.
 *
 * Right and wrong are said in words and marked with a glyph. The border tint
 * is a hint on top of that, never the message.
 *
 * The score is deliberately not here. This component used to open with its own
 * heading and its own `ScoreIndicator`, which meant that on every practice
 * where the review opened, the student met the same ring twice — once from the
 * runner above and once from here, under two different headings, in two
 * different alignments, with the buttons in between. Two components had each
 * decided they owned the score. The runner owns it: it already holds the
 * practice title and the actions, and the handoff's results layout is one
 * score indicator, then one primary action, then history. What is left here is
 * the only thing this query can say that the runner cannot.
 */
export function CompletionReview({ attemptId }: CompletionReviewProps) {
  const t = useTranslate()
  const { data, loading, error } = useQuery(MyCompletedAttemptDocument, {
    variables: { attemptId },
  })

  if (loading) return <LoadingState>{t('attempt.review.loading')}</LoadingState>
  if (error) {
    return (
      <p role="status" className="text-body text-text-secondary">
        {t('attempt.review.unavailable')}
      </p>
    )
  }
  if (!data) return null

  const review = data.myCompletedAttempt
  const wrong = review.questionCount - review.correctCount

  return (
    <Section id="question-review" title={t('attempt.review.title')}>
      <div className="flex flex-col gap-1">
        {/* Not the score again — how much of it is worth going back over,
            which is the one figure the ring above cannot give. */}
        <p className="text-body-sm text-text-secondary tabular-nums">
          {wrong === 0
            ? t('attempt.review.allRight')
            : wrong === 1
              ? t('attempt.review.toLookAtOne')
              : t('attempt.review.toLookAtOther', [wrong])}
        </p>
      </div>

      <ol className="divide-y divide-border">
        {review.questions.map((question) => (
          <li key={question.id}>
            <ReviewQuestion
              number={question.position + 1}
              prompt={<RichText inline>{question.prompt}</RichText>}
              isCorrect={question.wasCorrect}
              resultLabel={
                question.wasCorrect ? t('attempt.review.correct') : t('attempt.review.notRight')
              }
              answer={
                question.matches.length > 0 ? (
                  /* A matching question is marked pair by pair, so its rows show
                 whether or not the question as a whole was right: a student who
                 got three of four needs to see which one was the fourth. */
                  <MatchingResult
                    rows={question.matches.map((row) => ({
                      leftText: row.leftText,
                      yourAnswer: row.yourAnswer,
                      correctAnswer: row.correctAnswer,
                      isCorrect: row.wasCorrect,
                    }))}
                    yourLabel={t('attempt.review.youPut')}
                    correctLabel={t('attempt.review.theAnswer')}
                  />
                ) : question.wasCorrect ? undefined : question.yourOrder.length > 0 ||
                  question.correctOrder.length > 0 ? (
                  /* An ordering question: two numbered lists, because an answer
                 that is a sequence read out as a sentence is not readable.
                 The server sends `correctOrder` only when it may be seen. */
                  <AssociationResult
                    yourOrder={question.yourOrder}
                    correctOrder={question.correctOrder}
                    yourLabel={t('attempt.review.youPut')}
                    correctLabel={t('attempt.review.theAnswer')}
                  />
                ) : (
                  <div className="flex flex-col gap-1 text-body">
                    <p>
                      <span className="font-semibold text-text-strong">
                        {t('attempt.review.youPutLabel')}
                      </span>
                      {question.yourAnswer ?? (
                        <span className="text-text-secondary">{t('attempt.review.nothing')}</span>
                      )}
                    </p>
                    {question.correctAnswer ? (
                      <p>
                        <span className="font-semibold text-accent-text">
                          {t('attempt.review.theAnswerLabel')}
                        </span>
                        {question.correctAnswer}
                      </p>
                    ) : null}
                  </div>
                )
              }
              explanation={
                question.explanation ? (
                  <RichText inline>{question.explanation}</RichText>
                ) : undefined
              }
            />
          </li>
        ))}
      </ol>
    </Section>
  )
}
